import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create main axios instance
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      console.error('Network Error:', error.message);
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    // If token expired, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/users/token/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    // Extract meaningful error message
    const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.error || 
                        error.response?.data?.message ||
                        `HTTP ${error.response?.status}: ${error.response?.statusText}` ||
                        'An unexpected error occurred';

    console.error('API Error:', errorMessage, error.response);
    return Promise.reject(new Error(errorMessage));
  }
);

// Helper methods for common HTTP operations
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};

// Auth specific methods
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/users/login/', credentials);
    return response.data;
  },
  
  register: {
    athlete: async (userData) => {
      const response = await api.post('/users/register/athlete/', userData);
      return response.data;
    },
    coach: async (userData) => {
      const response = await api.post('/users/register/coach/', userData);
      return response.data;
    },
  },
  
  onboarding: {
    complete: async (onboardingData) => {
      const response = await api.put('/users/onboarding/athlete/', onboardingData);
      return response.data;
    },
  },
  
  user: {
    getCurrentUser: async () => {
      const response = await api.get('/users/me/');
      return response.data;
    },
  },
  
  token: {
    refresh: async (refreshToken) => {
      const response = await axios.post(`${API_URL}/api/users/token/refresh/`, {
        refresh: refreshToken,
      });
      return response.data;
    },
  },
};

// Plans/Workout specific methods
export const plansAPI = {
  getPlans: async () => {
    const response = await api.get('/plans/');
    return response.data;
  },
  
  getPlan: async (planId) => {
    const response = await api.get(`/plans/${planId}/`);
    return response.data;
  },
  
  getExercises: async () => {
    const response = await api.get('/plans/exercises/');
    return response.data;
  },
};

export default apiClient;